/**
 * Password strength scorer — OWASP + NCSC balanced tier.
 *
 * Returns { level, label, hint, checks } where:
 *   level  = 'weak' | 'average' | 'strong'
 *   label  = user-facing status (e.g. 'Weak — add a number and uppercase')
 *   checks = { length8, length12, lower, upper, digit, symbol, classes }
 *
 * Rules (matches the plan the user picked, option A):
 *   Weak     : length < 8 OR fewer than 3 of {lower, upper, digit, symbol}
 *   Average  : length >= 8 AND at least 3 of the 4 classes
 *   Strong   : length >= 12 AND all 4 classes
 *
 * The submit form disables while level === 'weak'.
 */
export function scorePassword(pw) {
  const p = pw || '';
  const lower = /[a-z]/.test(p);
  const upper = /[A-Z]/.test(p);
  const digit = /\d/.test(p);
  const symbol = /[^A-Za-z0-9]/.test(p);
  const classes = [lower, upper, digit, symbol].filter(Boolean).length;
  const length8 = p.length >= 8;
  const length12 = p.length >= 12;

  let level;
  if (!length8 || classes < 3) level = 'weak';
  else if (length12 && classes === 4) level = 'strong';
  else level = 'average';

  const missing = [];
  if (!length8) missing.push('8+ characters');
  if (!lower) missing.push('a lowercase letter');
  if (!upper) missing.push('an uppercase letter');
  if (!digit) missing.push('a number');
  if (!symbol) missing.push('a symbol (e.g. !@#)');

  let label;
  if (level === 'weak') {
    label = missing.length ? `Weak — add ${missing.slice(0, 2).join(' and ')}` : 'Weak';
  } else if (level === 'average') {
    label = length12 ? 'Average — add a symbol for a stronger password' : 'Average — add more length for extra strength';
  } else {
    label = 'Strong — nice one';
  }

  return {
    level,
    label,
    checks: { length8, length12, lower, upper, digit, symbol, classes },
  };
}
